/** Premium Go7 map themes — Google Maps is render-only.
 * Colors match `styles/globals.css` GO7 tokens.
 */

export const GO7_COLORS = {
  primary: '#E0001B',
  primaryDark: '#C00017',
  accent: '#00B277',
  warning: '#FFB020',
  white: '#FFFFFF',
  dark: '#111111',
  darkGray: '#1a1d21',
  muted: '#6B6B6B',
  completedRoute: '#00B277',
  remainingRoute: '#E0001B',
  rider: '#E0001B',
} as const;

/** Soft light map — home / job preview */
export const GO7_MAP_STYLE_LIGHT = [
  { elementType: 'geometry', stylers: [{ color: '#f4f5f7' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#5f6368' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#d0d3d8' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e6f2ea' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#dadce0' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  // Subtle brand wash on highways
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#fde8ea' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#f0c4c8' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#8a3a40' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c5d8f0' }] },
];

/** Dark nav map — matches ActiveJob chrome (#0a0a0a / #111) */
export const GO7_MAP_STYLE_DARK = [
  { elementType: 'geometry', stylers: [{ color: '#121418' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#9aa0a6' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#121418' }, { weight: 2 }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#2a2e34' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#162019' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2f36' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#3a4048' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#b0b5bb' }] },
  // Brand-tinted highways so the GO7 red route reads clearly
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3a2426' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#5c3034' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#e8a0a6' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0c1a28' }] },
];

export const GO7_MAP_STYLE = GO7_MAP_STYLE_LIGHT;

/**
 * Active-job turn-by-turn navigation map.
 * Light + high-contrast so street names and roads are clearly visible (like
 * Google Maps / Uber) — a near-black style hides labels and makes the map look
 * empty, which riders complained about.
 */
export const GO7_MAP_STYLE_NAV = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#333333' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#d0d3d8' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e6f2ea' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#d9d9d9' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#444444' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#fde8ea' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#f0c4c8' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#8a3a40' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c5d8f0' }] },
];

export function isNightMode(): boolean {
  const h = new Date().getHours();
  return h < 6 || h >= 19;
}

/** Home / preview maps — auto day/night */
export function getActiveMapStyle(): typeof GO7_MAP_STYLE_LIGHT {
  return isNightMode() ? GO7_MAP_STYLE_DARK : GO7_MAP_STYLE_LIGHT;
}

/** Active-job turn-by-turn — clear light map so street names are visible. */
export function getNavMapStyle(): typeof GO7_MAP_STYLE_NAV {
  return GO7_MAP_STYLE_NAV;
}

export const GO7_ROUTE_COLORS = {
  shadow: '#000000',
  active: '#374151',
  progress: GO7_COLORS.remainingRoute,
  completed: GO7_COLORS.completedRoute,
  pickup: GO7_COLORS.accent,
  delivery: GO7_COLORS.primary,
  rider: GO7_COLORS.rider,
  riderArrow: '#4285F4',
  job: '#FFB020',
} as const;
